import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  X, FolderSimple, ArrowsClockwise, CaretLeft, ArrowSquareIn, Warning,
} from "@phosphor-icons/react";
import { listFolder } from "../../api/microsoft";

const DD_FOLDER_NAME = "DD Project Planner";

/**
 * MoveFolderPicker
 *
 * A modal for selecting a destination folder when moving a driveItem.
 *
 * Props:
 *   rootFolderId  — the "DD Project Planner" folder ID (starting point)
 *   movingItemId  — the ID of the item being moved (excluded from picker)
 *   currentParentId — current parent folder (shown as "current location")
 *   onMove(destinationFolderId) — called with the chosen destination folder ID
 *   onClose()
 */
export default function MoveFolderPicker({ rootFolderId, movingItemId, currentParentId, onMove, onClose }) {
  const [stack, setStack] = useState([{ id: rootFolderId, name: DD_FOLDER_NAME }]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [error, setError] = useState(null);

  const currentFolder = stack[stack.length - 1];

  const loadFolders = useCallback(async (folderId) => {
    setLoading(true);
    setError(null);
    try {
      const items = await listFolder(folderId);
      // Only show folders, exclude the item being moved
      setFolders(items.filter((i) => i.folder && i.id !== movingItemId));
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [movingItemId]);

  useEffect(() => {
    loadFolders(currentFolder.id);
  }, [currentFolder.id, loadFolders]);

  const navigateInto = (folder) => {
    setStack((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const navigateBack = () => {
    if (stack.length <= 1) return;
    setStack((prev) => prev.slice(0, -1));
  };

  const navigateTo = (index) => {
    setStack((prev) => prev.slice(0, index + 1));
  };

  const handleMoveHere = async () => {
    if (currentFolder.id === currentParentId) {
      toast.error("The item is already in this folder.");
      return;
    }
    setMoving(true);
    try {
      await onMove(currentFolder.id);
    } finally {
      setMoving(false);
    }
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="move-picker-backdrop"
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-sm shadow-2xl flex flex-col"
        style={{ maxHeight: "70vh" }}
        data-testid="move-picker-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0">
          <p className="text-sm font-semibold text-slate-800">Move to…</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="move-picker-close">
            <X size={16} />
          </button>
        </div>

        {/* Breadcrumb */}
        <div className="flex items-center gap-1 px-4 py-2 border-b border-slate-100 text-xs text-slate-500 flex-shrink-0 overflow-x-auto min-w-0">
          {stack.length > 1 && (
            <button onClick={navigateBack} className="p-1 text-slate-400 hover:text-slate-700 flex-shrink-0">
              <CaretLeft size={12} />
            </button>
          )}
          {stack.map((crumb, i) => (
            <span key={crumb.id} className="flex items-center gap-1 flex-shrink-0">
              {i > 0 && <span className="text-slate-300 mx-0.5">/</span>}
              <button
                onClick={() => i < stack.length - 1 && navigateTo(i)}
                className={`whitespace-nowrap ${i === stack.length - 1 ? "font-semibold text-slate-800 cursor-default" : "hover:text-primary hover:underline"}`}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </div>

        {/* Folder list */}
        <div className="flex-1 overflow-y-auto" data-testid="move-picker-folder-list">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-400">
              <ArrowsClockwise size={18} className="animate-spin" />
              <span className="text-xs">Loading…</span>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center px-4">
              <Warning size={18} className="text-red-400" />
              <p className="text-xs text-slate-500">{error}</p>
            </div>
          ) : folders.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-400">
              <FolderSimple size={24} className="text-slate-300" />
              <p className="text-xs">No subfolders here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {folders.map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => navigateInto(folder)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-slate-50 text-left transition-colors"
                  data-testid={`move-folder-${folder.name}`}
                >
                  <FolderSimple size={15} className="text-[#0078D4] flex-shrink-0" />
                  <span className="text-sm text-slate-700 truncate">{folder.name}</span>
                  <CaretLeft size={12} className="text-slate-300 ml-auto rotate-180 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 flex-shrink-0 bg-slate-50">
          <p className="text-xs text-slate-400 truncate flex-1 min-w-0">
            Moving to: <span className="font-medium text-slate-600">{currentFolder.name}</span>
          </p>
          <button
            onClick={handleMoveHere}
            disabled={moving || currentFolder.id === currentParentId}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-white text-xs font-medium rounded-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
            data-testid="move-here-btn"
          >
            {moving ? (
              <ArrowsClockwise size={12} className="animate-spin" />
            ) : (
              <ArrowSquareIn size={12} />
            )}
            Move here
          </button>
        </div>
      </div>
    </div>
  );
}
